import toggleBoolStringAttr from 'views/utils/toggleBoolStringAttribute'
import { ARIA_HIDDEN, ARIA_EXPANDED, ARIA_CONTROLS } from './consts'

/**
 * expander
 * ========
 *
 * The KEY_TAG here is preferaibily a <button> with two mandatory attributes:
 * - data-clientside-hook: containing "expander__button" (can coexists with other values)
 * - aria-controls: contains the id of the EXPANDING_TARGET element
 *
 * the expander will be enabled IF AND ONLY IF
 * - those two attributes are set
 * AND
 * - can be found a node with an id attribute equal to the value found in the aria-controls
 *
 * Once effective it will take actions on click on the KEY_TAG
 * The effect of the first click on the KEY_TAG depends on the initial state that is
 *  determined by the presence of a specific attribute=value on the EXPANDING_TARGET
 *  and this attribute should be set whenever the EXPANDING_TARGET needs to be expanded
 *  right from the beginning.
 * To initialize the EXPANDING_TARGET as expanded we should
 * explicitly add the attribute "aria-expanded" as "true" to KEY_TAG
 * otherwise it will be initialized as non-expanded
 *
 * Now that we know how to set the initial state, let's get a look at the transitions:
 * - toggle 2 expanded
 *    KEY_TAG aria-hidden attribute is removed
 *    EXPANDING_TARGET aria-expanded attribute is set to 'true'
 *    focus on EXPANDING_TARGET
 *    attempt to smooth expand expand to the needed height
 *
 * - toggle 2 non-expanded
 *    KEY_TAG aria-hidden attribute is set to 'true'
 *    EXPANDING_TARGET aria-expanded attribute is removed
 *    attempt to smooth expand collapse to zero height
 *
 * @example
 *    <button
 *      data-clientside-hook="expander__button"    <-- KEY_TAG
 *      aria-controls="%TARGET_ID%"                <-- TARGET_ID node
 *    >
 *      Press me
 *    </button>
 *    <div id="%TARGET_ID%">whatever</div>         <-- EXPANDING_TARGET node with id=%TARGET_ID%
 *
 * @returns {Array.<{button: HTMLElement, body: HTMLElement}>}
 * Of literal objects each one corresponding to an enabled button; the object will contain
 * - a redefence to the KEY_TAG node
 * - a reference to the EXPANDING_TARGET node
 */
export default () => {
  /** @type {NodeListOf<HTMLElement>} */
  const expanderButtonElementList = document.querySelectorAll(
    '[data-clientside-hook~="expander__button"]'
  )
  const expanderButtonElements = [...expanderButtonElementList]

  return expanderButtonElements.reduce(
    (reducedCollection, expanderButtonElement) => {
      const bodyId = expanderButtonElement.getAttribute(ARIA_CONTROLS)
      const expanderBodyElement = document.getElementById(bodyId)

      if (!bodyId || !expanderBodyElement) {
        return reducedCollection
      }

      let isExpanded =
        expanderButtonElement.getAttribute(ARIA_EXPANDED) === 'true'
      const expanderBodyElementTransition =
        expanderBodyElement.style.transition || 'height 0.5s'

      function ariaToggle(visible) {
        toggleBoolStringAttr(expanderBodyElement, ARIA_HIDDEN, !visible)
        toggleBoolStringAttr(expanderButtonElement, ARIA_EXPANDED, visible)
      }

      function hideBody(init) {
        ariaToggle(false)

        isExpanded = false
        expanderBodyElement.style.transition = ''

        requestAnimationFrame(() => {
          // on the next frame (as soon as the previous style change has taken effect),
          // explicitly set the element’s height to its current pixel height, so we
          // aren’t transitioning out of ‘auto’
          expanderBodyElement.style.height = `${expanderBodyElement.scrollHeight}px`
          expanderBodyElement.style.transition = expanderBodyElementTransition

          requestAnimationFrame(() => {
            // on the next frame (as soon as the previous style change has taken effect),
            // have the element transition to height: 0
            expanderBodyElement.style.height = '0px'
            expanderBodyElement.style.overflow = null

            function displayNone() {
              // After the hideBody animation is done, the content needs to be removed
              // from the document flow or it will sometimes cause spacing issues (for example
              // displaying a scrollbar inside modals when there is only empty space at the
              // bottom).
              // After the transition on expanderBodyElement ends, the event listener
              // should be removed again.
              expanderBodyElement.removeEventListener(
                'transitionend',
                displayNone
              )
              expanderBodyElement.style.display = 'none'
            }

            if (!init) {
              expanderBodyElement.addEventListener('transitionend', displayNone)
            }

            expanderButtonElement.blur()
          })
        })
      }

      function showBody() {
        ariaToggle(true)

        expanderBodyElement.focus()
        isExpanded = true
        // The element must be a block element before the height is set (or it will be 0)!
        expanderBodyElement.style.display = 'block'
        // have the element transition to the height of its inner content
        expanderBodyElement.style.height = `${expanderBodyElement.scrollHeight}px`

        function onTransitionEnd() {
          // when the next css transition finishes (which should be the one we just triggered)
          // remove this event listener so it only gets triggered once
          expanderBodyElement.removeEventListener(
            'transitionend',
            onTransitionEnd
          )
          expanderBodyElement.style.height = 'auto'
          expanderBodyElement.style.overflow = 'visible'
          expanderBodyElement.style.display = 'block'
        }

        expanderBodyElement.addEventListener('transitionend', onTransitionEnd)
      }

      function toggleBody() {
        ;(isExpanded ? hideBody : showBody)()
      }

      expanderButtonElement.addEventListener('click', toggleBody)

      if (!isExpanded) {
        hideBody(true)
      }

      reducedCollection.push({
        button: expanderButtonElement,
        body: expanderBodyElement
      })

      return reducedCollection
    },
    []
  )
}
